import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    InputAdornment,
    InputLabel,
    TextField,
    Typography,
} from "@mui/material";
import { useStyles } from "./RCAIssuesFilters.styles";
import { useTranslation } from "react-i18next";
import { IRCAIssuesFilters } from "./RCAIssuesFiltersInterfaces";
import { RCA_STATUSES } from "constants/constants";
import RCAStatusDropdown from "pages/Corporate/RCA/components/RCAStatusDropdown";
import RCAUsersDropdown from "pages/Corporate/RCA/components/RCAUsersDropdown";
import { useContext, useEffect, useState } from "react";
import RCAExecutiveMillFilter from "pages/Corporate/RCA/components/RCAExecutiveMillFilter";
import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import useRolesPermissions from "hooks/useRolesPermissions";
import RCAMeetingTypesDropdown from "pages/Corporate/RCA/components/RCAMeetingTypesDropdown/RCAMeetingTypesDropdown";
import { RCAIssueFiltersContext } from "contexts/RCAIssueFiltersContext/RCAIssueFiltersContext";
import DefaultFiltersButton from "pages/Corporate/RCA/components/DefaultFiltersButton/DefaultFiltersButton";

const RCAIssuesFilters = ({ disableMillExecutiveFilter, onClickAddNewIssue }: IRCAIssuesFilters) => {
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const { t } = useTranslation();
    const {
        filters: filtersSelected,
        handleFiltersChange: onSetFilters,
        loadingDefaultOperations,
        isFiltersDefault,
        handleRemoveDefaultFilters,
        handleSetFiltersDefault,
        userFiltersDefault,
    } = useContext(RCAIssueFiltersContext);

    const { RCA_PERMISSIONS } = useRolesPermissions();

    const [userMillId, setUserMillId] = useState<number[] | null>(null);
    const [statusIds, setStatusIds] = useState<(string | number)[] | null>(null);
    const [userIds, setUserIds] = useState<(string | number)[] | null>(null);
    const [meetingTypeIds, setMeetingTypeIds] = useState<(string | number)[] | null>(null);

    useEffect(() => {
        if (!userMillId || !statusIds || !userIds || !meetingTypeIds) return;
        if ((filtersSelected.millsIds as number[]).includes(0)) {
            onSetFilters({
                ...filtersSelected,
                millsIds: userMillId,
                statusesIds: statusIds as (string | number)[],
                ownerIds: userIds as (string | number)[],
                meetingTypeIds: meetingTypeIds,
                totalMeetingTypes: meetingTypeIds.length,
            });
        }

        setStatusIds(null);
        setUserMillId(null);
    }, [filtersSelected, onSetFilters, statusIds, userIds, userMillId, meetingTypeIds]);

    return (
        <section className={classes.rcaIssuesFiltersMainWrapper}>
            <div className={`${mainClasses.contentHeaderActions} ${classes.mainFilters}`}>
                <span className={`${classes.title} ${mainClasses.contentTitle}`}>{t("rca.issues.title")}</span>
                <div className={classes.showCompletedCheck}>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filtersSelected.showCompleted}
                                    value={filtersSelected.statusesIds.includes(RCA_STATUSES.COMPLETED)}
                                    color="info"
                                    onChange={(_, checked) => {
                                        onSetFilters({
                                            ...filtersSelected,
                                            showCompleted: checked,
                                            statusesIds: checked
                                                ? [...filtersSelected.statusesIds, RCA_STATUSES.COMPLETED]
                                                : filtersSelected.statusesIds.filter(
                                                      (statusId) => statusId !== RCA_STATUSES.COMPLETED,
                                                  ),
                                        });
                                    }}
                                />
                            }
                            label={t("rca.issues.filters.show_completed")}
                        />
                    </FormGroup>
                </div>

                <RCAExecutiveMillFilter
                    disableMillExecutiveFilter={disableMillExecutiveFilter}
                    onComponentLoaded={(userMillIds) => {
                        setUserMillId(userMillIds);
                    }}
                    selectedMillsIds={filtersSelected.millsIds}
                    onMillSelectedChange={(values) => {
                        onSetFilters({
                            ...filtersSelected,
                            millsIds: values,
                        });
                    }}
                    onSetExecutiveMillFilters={(filters) => {
                        onSetFilters({
                            ...filtersSelected,
                            millsIds: filters.millsIds,
                        });
                    }}
                />

                <Box>
                    <InputLabel>
                        <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                            {t("rca.executive_mill_filter_labels.owner")}
                        </Typography>
                    </InputLabel>
                    <RCAUsersDropdown
                        multiple
                        value={filtersSelected.ownerIds}
                        handleOnComponentMount={(userIds) => {
                            setUserIds(userIds);
                        }}
                        onChange={(values) => {
                            onSetFilters({
                                ...filtersSelected,
                                ownerIds: values,
                            });
                        }}
                    />
                </Box>
                <Box>
                    <InputLabel>
                        <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                            {t("rca.executive_mill_filter_labels.status")}
                        </Typography>
                    </InputLabel>
                    <RCAStatusDropdown
                        multiple
                        hideLabel
                        fullWidth={false}
                        handleOnComponentMount={(statusIds) => {
                            setStatusIds(statusIds);
                        }}
                        value={filtersSelected.statusesIds}
                        asFilter
                        excludeStatuses={filtersSelected.showCompleted ? [] : [RCA_STATUSES.COMPLETED]}
                        handleChange={(values) => {
                            onSetFilters({
                                ...filtersSelected,
                                statusesIds: values,
                            });
                        }}
                    />
                </Box>
                <Box>
                    <InputLabel>
                        <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                            {t("rca.rca_library.rca_detail.meeting_type")}
                        </Typography>
                    </InputLabel>
                    <RCAMeetingTypesDropdown
                        notSelectedOption
                        multiple
                        value={filtersSelected.meetingTypeIds}
                        handleOnComponentMount={(meetingTypeIds) => {
                            setMeetingTypeIds(meetingTypeIds);
                        }}
                        onChange={(values) => {
                            onSetFilters({
                                ...filtersSelected,
                                meetingTypeIds: values,
                            });
                        }}
                    />
                </Box>
                <DefaultFiltersButton
                    isDefault={isFiltersDefault}
                    loading={loadingDefaultOperations}
                    hasUserDefault={!!userFiltersDefault}
                    onRemoveDefault={handleRemoveDefaultFilters}
                    onSaveOrUpdate={() => handleSetFiltersDefault(filtersSelected)}
                    variant="contained"
                    color="info"
                    className={mainClasses.contentButton}
                />
                <Button
                    style={{ width: "180px" }}
                    disabled={!RCA_PERMISSIONS?.issues.canCreate}
                    variant="contained"
                    color="info"
                    className={mainClasses.contentButton}
                    onClick={onClickAddNewIssue}
                >
                    <AddCircleIcon />
                    {t("rca.issues.add_issue_button")}
                </Button>
            </div>
            <Box className={classes.bottomFilters}>
                <div className={classes.searchBar}>
                    <TextField
                        style={{ background: "white" }}
                        fullWidth
                        className={`${mainClasses.contentFormMultitextField}`}
                        name="description"
                        type="text"
                        variant="outlined"
                        value={filtersSelected.searchText}
                        placeholder={t("rca.issues.filters.search_keyword")}
                        onChange={(event) => {
                            onSetFilters({
                                ...filtersSelected,
                                searchText: event.target.value,
                            });
                        }}
                        size="small"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>
            </Box>
        </section>
    );
};

export default RCAIssuesFilters;
